import axios from 'axios';
import { getDeviceId, getDeviceName } from '../utils/deviceId';

// URL base de la API
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/**
 * Obtiene la lista paginada de series
 * @param {number} page - Número de página
 * @param {number} limit - Cantidad de series por página
 * @returns {Promise} - Promesa con los resultados
 */
export const getAllSeries = async (page = 1, limit = 10) => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_URL}/api/v1/series?page=${page}&limit=${limit}`,
      { 
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error al obtener series:', error);
    return { success: false, error: error.response?.data?.message || 'Error al cargar series' };
  }
};

/**
 * Busca series por término
 * @param {string} query - Término de búsqueda
 * @param {number} page - Número de página
 * @param {number} limit - Cantidad de series por página
 * @returns {Promise} - Promesa con los resultados
 */
export const searchSeries = async (query, page = 1, limit = 10) => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_URL}/api/v1/series/search?query=${encodeURIComponent(query)}&page=${page}&limit=${limit}`,
      { 
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error al buscar series:', error);
    return { success: false, error: error.response?.data?.message || 'Error al buscar series' };
  }
};

/**
 * Obtiene una serie por su ID
 * @param {number} id - ID de la serie
 * @returns {Promise} - Promesa con los resultados
 */
export const getSeriesById = async (id) => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_URL}/api/v1/series/${id}?withSeasons=true`,
      { 
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        }
      }
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Error al obtener detalles de la serie:', error);
    return { 
      success: false, 
      error: error.response?.data?.message || 'Error al cargar la serie',
      status: error.response?.status
    };
  }
};

/**
 * Obtiene las temporadas de una serie
 * @param {number} seriesId - ID de la serie
 * @returns {Promise} - Promesa con los resultados
 */
export const getSeasonsBySeries = async (seriesId) => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_URL}/api/v1/seasons/content/series/${seriesId}`,
      { 
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error al obtener temporadas:', error);
    throw error;
  }
};

/**
 * Obtiene los episodios de una temporada
 * @param {number} seasonId - ID de la temporada
 * @returns {Promise} - Promesa con los resultados
 */
export const getEpisodesBySeason = async (seasonId) => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_URL}/api/v1/episodes/season/${seasonId}`,
      { 
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error al obtener episodios:', error);
    throw error;
  }
};

/**
 * Obtiene la información para reproducir un episodio
 * @param {number} episodeId - ID del episodio
 * @returns {Promise} - Promesa con los resultados
 */
export const getPlayEpisode = async (episodeId) => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_URL}/api/v1/episodes/play/${episodeId}`,
      { 
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          'user-device-id': getDeviceId() || 'web-default',
          'user-device-name': getDeviceName() || navigator.userAgent
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error al obtener episodio para reproducción:', error);
    throw error;
  }
};

/**
 * Agrega un episodio a una temporada de una serie
 * @param {number} seriesId - ID de la serie
 * @param {number} seasonNumber - Número de temporada
 * @param {Object} episodeData - Datos del episodio a crear
 * @returns {Promise<Object>} - Resultado de la creación
 */
export const addEpisodeToSeason = async (seriesId, seasonNumber, episodeData) => {
  try {
    const token = localStorage.getItem("token");
    
    // Primero obtenemos las temporadas para conseguir el seasonId
    const seasonsResponse = await getSeasonsBySeries(seriesId);
    
    if (!seasonsResponse.success || !seasonsResponse.data) {
      return { success: false, message: 'No se encontraron temporadas para esta serie' };
    }
    
    // Encontrar la temporada por su número
    const season = seasonsResponse.data.find(s => 
      s.seasonNumber === parseInt(seasonNumber)
    );
    
    if (!season) {
      return { success: false, message: `No se encontró la temporada ${seasonNumber}` };
    }
    
    // Ahora crear el episodio directamente en la temporada
    const response = await axios.post(
      `${API_URL}/api/v1/episodes`,
      {
        ...episodeData,
        seasonId: season.id
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error(`Error al agregar episodio a temporada ${seasonNumber} de la serie ${seriesId}:`, error);
    throw error;
  }
};

/**
 * Obtiene el último episodio disponible de una serie
 * @param {number} seriesId - ID de la serie
 * @returns {Promise<Object>} - Datos del último episodio disponible
 */
export const getLastEpisode = async (seriesId) => {
  try {
    // Primero obtenemos todas las temporadas de la serie
    const seasonsResponse = await getSeasonsBySeries(seriesId);
    
    if (!seasonsResponse.success || !seasonsResponse.data || seasonsResponse.data.length === 0) {
      return { success: false, message: 'No se encontraron temporadas para esta serie' };
    }
    
    // Ordenamos las temporadas por número (de menor a mayor)
    const sortedSeasons = [...seasonsResponse.data].sort((a, b) => a.seasonNumber - b.seasonNumber);
    
    // Tomamos la primera temporada para comenzar desde el principio
    const firstSeason = sortedSeasons[0];
    
    // Obtenemos los episodios de esta temporada
    const episodesResponse = await getEpisodesBySeason(firstSeason.id);
    
    if (!episodesResponse.success || !episodesResponse.data || episodesResponse.data.length === 0) {
      return { success: false, message: 'No se encontraron episodios para esta serie' };
    }
    
    // Ordenamos los episodios por número (de menor a mayor)
    const sortedEpisodes = [...episodesResponse.data].sort((a, b) => a.episodeNumber - b.episodeNumber);
    
    // Tomamos el primer episodio para comenzar desde el principio
    const firstEpisode = sortedEpisodes[0];
    
    return { 
      success: true, 
      data: firstEpisode,
      message: 'Episodio encontrado'
    };
  } catch (error) {
    console.error(`Error al obtener el último episodio para serie ID ${seriesId}:`, error);
    return { 
      success: false, 
      message: 'Error al obtener el último episodio',
      error: error.message 
    };
  }
};

/**
 * Vista previa de un archivo CSV para importar series
 * @param {File} file - Archivo CSV
 * @returns {Promise} - Promesa con los resultados
 */
export const previewSeriesCsv = async (file) => {
  try {
    const token = localStorage.getItem("token");
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(
      `${API_URL}/api/v1/series/preview-csv`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error al previsualizar CSV de series:', error);
    throw error;
  }
};

/**
 * Importa series desde datos CSV
 * @param {Array} seriesData - Array de datos de series desde CSV
 * @returns {Promise} - Promesa con los resultados
 */
export const importSeriesCsv = async (seriesData) => {
  try {
    const token = localStorage.getItem("token");
    
    const response = await axios.post(
      `${API_URL}/api/v1/series/import`,
      { seriesData },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error al importar series desde CSV:', error);
    throw error;
  }
};

export default {
  getAllSeries,
  searchSeries,
  getSeriesById,
  getSeasonsBySeries,
  getEpisodesBySeason,
  getPlayEpisode,
  addEpisodeToSeason,
  getLastEpisode,
  previewSeriesCsv,
  importSeriesCsv
};
